import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import HelpTooltip from '../components/HelpTooltip';
import PwaInstall from './PwaInstall';

const API_BASE = process.env.REACT_APP_API_URL || '';
const LOGO = '/dailyreps3.png';

const CURRENT_YEAR = new Date().getFullYear();
const BIRTH_YEARS = Array.from({ length: 22 }, (_, i) => CURRENT_YEAR - 3 - i);

export default function ParentOnboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const freeTeamId = searchParams.get('team_id');
  const freePlan = searchParams.get('plan');
  const freeTeamName = searchParams.get('team_name');
  const trialInterval = searchParams.get('interval');
  const promoCode = searchParams.get('code');
  const stripePromo = searchParams.get('promo');
  const isClubPays = freePlan === 'club_pays' && freeTeamId;
  const isScholarship = freePlan === 'scholarship' && freeTeamId && promoCode;
  const isTrialSignup = !isClubPays && !isScholarship && !sessionId && freePlan && ['individual', 'family'].includes(freePlan);
  const isFreeSignup = freePlan === 'free' || (!sessionId && !isClubPays && !isScholarship && !isTrialSignup && !freePlan);
  const { loginParent } = useAuth();

  const [step, setStep] = useState(1);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [sessionError, setSessionError] = useState('');
  const [loading, setLoading] = useState(false);

  // Step 1: Account creation
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [accountError, setAccountError] = useState('');
  const [token, setToken] = useState(null);
  const [plan, setPlan] = useState('individual');
  const [timezone, setTimezone] = useState(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'America/New_York'; }
  });
  const [inviteTeamId, setInviteTeamId] = useState(null);

  // Step 2: Add players
  const [players, setPlayers] = useState([]);
  const [addingPlayer, setAddingPlayer] = useState(false);
  const [birthYear, setBirthYear] = useState('');
  const [usernameOptions, setUsernameOptions] = useState([]);
  const [selectedUsername, setSelectedUsername] = useState(null);
  const [playerIndividualPassword, setPlayerIndividualPassword] = useState('');
  const [confirmPlayerIndividualPassword, setConfirmPlayerIndividualPassword] = useState('');
  const [playerPassword, setPlayerPassword] = useState('');
  const [confirmPlayerPassword, setConfirmPlayerPassword] = useState('');
  const [createdPlayerCredentials, setCreatedPlayerCredentials] = useState(null);
  const [playerError, setPlayerError] = useState('');
  const [usernameLoading, setUsernameLoading] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [consentName, setConsentName] = useState('');
  const [playerFirstName, setPlayerFirstName] = useState('');
  const [playerLastName, setPlayerLastName] = useState('');

  // Step 3: Consent
  const [consented, setConsented] = useState(false);
  const [consentGivenInStep2, setConsentGivenInStep2] = useState(false);
  const [maxStep, setMaxStep] = useState(1);

  const goToStep = (s) => {
    if (s < 1 || s > maxStep) return;
    // Don't go back to step 1 once account is created
    if (s === 1 && token) return;
    setStep(s);
  };

  const advanceStep = (s) => {
    setStep(s);
    setMaxStep(prev => Math.max(prev, s));
  };

  // Affiliate tracking
  useEffect(() => {
    const s = document.createElement('script');
    s.src = 'https://affiliate-platform-production-f4fa.up.railway.app/track.js?pid=cmtdislyw024r2tx3gmysyb0g&v=1';
    s.async = true;
    document.head.appendChild(s);
    return () => { document.head.removeChild(s); };
  }, []);

  // Affiliate tracking (step 2 conversion)
  useEffect(() => {
    if (step !== 2) return;
    const s = document.createElement('script');
    s.src = 'https://affiliate-platform-production-f4fa.up.railway.app/track.js?pid=cmtdislyw024r2tx3gmysyb0g&v=2';
    s.async = true;
    document.head.appendChild(s);
    return () => { document.head.removeChild(s); };
  }, [step]);

  // Affiliate tracking (sale confirmation)
  useEffect(() => {
    if (!sessionId) return;
    const s = document.createElement('script');
    s.src = 'https://affiliate-platform-production-f4fa.up.railway.app/track.js?pid=cmtdislyw024r2tx3gmysyb0g&v=3';
    s.async = true;
    document.head.appendChild(s);
    return () => { document.head.removeChild(s); };
  }, [sessionId]);

  // Load session info
  useEffect(() => {
    if (isClubPays) {
      // Club pays: no Stripe session needed
      setSessionInfo({ plan: 'club_pays', team_id: freeTeamId });
      setPlan('club_pays');
      setInviteTeamId(freeTeamId);
      return;
    }
    if (isScholarship) {
      // Scholarship: no Stripe session needed
      setSessionInfo({ plan: 'scholarship', team_id: freeTeamId });
      setPlan('scholarship');
      setInviteTeamId(freeTeamId);
      return;
    }
    if (isTrialSignup) {
      // Trial signup: no Stripe session needed
      setSessionInfo({ plan: freePlan, interval: trialInterval || 'monthly' });
      setPlan(freePlan);
      return;
    }
    if (isFreeSignup) {
      // Free signup: no Stripe, no team required
      setSessionInfo({ plan: 'free' });
      setPlan('free');
      return;
    }
    if (!sessionId) {
      setSessionError('No checkout session found. Please start from the order page.');
      return;
    }
    fetch(`${API_BASE}/api/order/session/${sessionId}`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error('Invalid session')))
      .then(data => {
        setSessionInfo(data);
        setEmail(data.email || '');
        setPlan(data.plan || 'individual');
        setInviteTeamId(data.team_id || null);
      })
      .catch(err => setSessionError(err.message));
  }, [sessionId]); // eslint-disable-line

  const maxPlayers = plan === 'family' ? 8 : 1;

  // Generate usernames
  const generateUsernames = async () => {
    if (!birthYear) return;
    setUsernameLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/username/generate?birth_year=${birthYear}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate usernames');
      setUsernameOptions(data.options || []);
      setSelectedUsername(null);
    } catch (err) {
      setPlayerError(err.message);
    } finally {
      setUsernameLoading(false);
    }
  };

  useEffect(() => {
    if (birthYear && token) generateUsernames();
  }, [birthYear, token]); // eslint-disable-line

  // Step 1: Create account
  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setAccountError('');
    if (password !== confirmPassword) { setAccountError('Passwords do not match'); return; }
    if (password.length < 8) { setAccountError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      let apiUrl, body;
      const affiliateRef = searchParams.get('aff');
      if (isClubPays) {
        apiUrl = `${API_BASE}/api/order/complete-parent-free`;
        body = { email, password, first_name: firstName, last_name: lastName, team_id: freeTeamId };
        if (promoCode) body.invite_code = promoCode;
        if (affiliateRef) body.affiliate_ref = affiliateRef;
      } else if (isScholarship) {
        apiUrl = `${API_BASE}/api/order/complete-parent-free`;
        body = { email, password, first_name: firstName, last_name: lastName, team_id: freeTeamId, promo_code: promoCode };
        if (affiliateRef) body.affiliate_ref = affiliateRef;
      } else if (isTrialSignup) {
        apiUrl = `${API_BASE}/api/order/complete-parent-trial`;
        body = { email, password, first_name: firstName, last_name: lastName, plan: freePlan, interval: trialInterval || 'monthly' };
        if (affiliateRef) body.affiliate_ref = affiliateRef;
        const t = searchParams.get('trialxyz') || localStorage.getItem('trialxyz');
        if (t) body.trial_days = parseInt(t, 10);
        if (stripePromo) body.promo_code = stripePromo;
      } else if (isFreeSignup) {
        apiUrl = `${API_BASE}/api/order/complete-parent-free`;
        body = { email, password, first_name: firstName, last_name: lastName };
        if (affiliateRef) body.affiliate_ref = affiliateRef;
      } else {
        apiUrl = `${API_BASE}/api/order/complete-parent`;
        body = { session_id: sessionId, password, first_name: firstName, last_name: lastName };
      }
      body.timezone = timezone;
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Account creation failed');
      setToken(data.token);
      setPlan(data.plan || plan);
      setInviteTeamId(data.invite_team_id || inviteTeamId);
      localStorage.setItem('token', data.token);
      if (data.already_completed) {
        navigate('/parent');
        return;
      }
      advanceStep(2);
      setAddingPlayer(true);
    } catch (err) {
      setAccountError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const isTeamSignup = !!inviteTeamId;

  // Step 2: Add a player
  const handleAddPlayer = async (e) => {
    e.preventDefault();
    setPlayerError('');
    if (!playerFirstName.trim() || !playerLastName.trim()) { setPlayerError('First and last name are required'); return; }
    if (!isTeamSignup) {
      if (!selectedUsername) { setPlayerError('Please select a username'); return; }
    }
    if (isTeamSignup) {
      if (playerPassword.length < 6) { setPlayerError('Password must be at least 6 characters'); return; }
      if (playerPassword !== confirmPlayerPassword) { setPlayerError('Passwords do not match'); return; }
    } else {
      if (playerIndividualPassword.length < 6) { setPlayerError('Password must be at least 6 characters'); return; }
      if (playerIndividualPassword !== confirmPlayerIndividualPassword) { setPlayerError('Passwords do not match'); return; }
    }
    const playerAge = CURRENT_YEAR - parseInt(birthYear);
    if (playerAge < 13 && !consentAccepted) { setPlayerError('Parental consent is required for children under 13'); return; }
    if (playerAge < 13 && consentName.trim().length < 2) { setPlayerError('Please enter your full name for consent'); return; }
    setLoading(true);
    try {
      const body = {
        birth_year: parseInt(birthYear),
        team_id: inviteTeamId || undefined,
      };
      if (isTeamSignup) {
        body.password = playerPassword;
        body.first_name = playerFirstName.trim();
        body.last_name = playerLastName.trim();
      } else {
        body.password = playerIndividualPassword;
        body.first_name = playerFirstName.trim();
        body.last_name = playerLastName.trim();
        body.generated_username = selectedUsername.generated_username;
        body.soccer_word = selectedUsername.soccer_word;
        body.creature = selectedUsername.creature;
      }
      if (playerAge < 13) {
        body.consent_accepted = true;
        body.parent_consent_name = consentName.trim();
      }
      const res = await fetch(`${API_BASE}/api/parent/kids`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add player');
      if (playerAge < 13) setConsentGivenInStep2(true);
      setPlayers([...players, data.kid]);
      const playerDisplayName = data.kid.first_name
        ? `${data.kid.first_name} ${data.kid.last_name}`
        : (data.kid.generated_username || data.kid.username);
      const playerPwd = isTeamSignup ? playerPassword : playerIndividualPassword;
      if (data.kid.username || data.kid.generated_username) {
        setCreatedPlayerCredentials({
          name: playerDisplayName,
          username: data.kid.username || data.kid.generated_username,
          password: playerPwd,
        });
      }
      setAddingPlayer(false);
      setBirthYear('');
      setUsernameOptions([]);
      setSelectedUsername(null);
      setPlayerIndividualPassword('');
      setConfirmPlayerIndividualPassword('');
      setPlayerPassword('');
      setConfirmPlayerPassword('');
      setConsentAccepted(false);
      setConsentName('');
      setPlayerFirstName('');
      setPlayerLastName('');
    } catch (err) {
      setPlayerError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePlayersDone = () => {
    if (players.length === 0) { setPlayerError('Please add at least one player'); return; }
    if (consentGivenInStep2) {
      advanceStep(4);
    } else {
      advanceStep(3);
    }
  };

  // Step 3: Consent
  const handleConsent = () => {
    if (!consented) return;
    advanceStep(4);
  };

  // Step 4: PWA Install done
  const handleDone = async () => {
    try {
      await loginParent(email, password);
    } catch (e) {
      // Token already set, just navigate
    }
    navigate('/parent');
  };

  if (!isClubPays && !isTrialSignup && !isFreeSignup && sessionError) {
    return (
      <div className="order-page">
        <header className="order-header"><div className="order-wrap">
          <div className="order-logo-row">
            <img src={LOGO} alt="Train Daily" className="order-logo" />
            <span className="order-brand">Train Daily</span>
          </div>
        </div></header>
        <main className="order-main"><div className="order-wrap">
          <div className="order-error-card"><h2>Oops</h2><p>{sessionError}</p></div>
        </div></main>
      </div>
    );
  }

  if (!sessionInfo && step === 1) {
    return <div className="order-page"><div className="loading"><div className="spinner" /></div></div>;
  }

  return (
    <div className="order-page">
      <header className="order-header"><div className="order-wrap">
        <div className="order-logo-row">
          <img src={LOGO} alt="Train Daily" className="order-logo" />
          <span className="order-brand">Train Daily</span>
        </div>
      </div></header>

      <main className="order-main"><div className="order-wrap">
        {/* Progress */}
        <div className="onboarding-nav">
          <button
            className="onboarding-arrow"
            onClick={() => goToStep(step - 1)}
            disabled={step <= 1 || (step === 2 && token)}
            type="button"
            aria-label="Previous step"
          >
            <i className="fas fa-arrow-left"></i>
          </button>
          <div className="onboarding-progress">
            {[1,2,3,4].map(s => (
              <div
                key={s}
                className={`onboarding-dot ${step >= s ? 'active' : ''} ${step === s ? 'current' : ''} ${s <= maxStep && !(s === 1 && token) ? 'clickable' : ''}`}
                onClick={() => goToStep(s)}
                role="button"
                tabIndex={s <= maxStep && !(s === 1 && token) ? 0 : -1}
              >
                {step > s ? <i className="fas fa-check"></i> : s}
              </div>
            ))}
          </div>
          <button
            className="onboarding-arrow"
            onClick={() => goToStep(step + 1)}
            disabled={step >= maxStep}
            type="button"
            aria-label="Next step"
          >
            <i className="fas fa-arrow-right"></i>
          </button>
        </div>

        {/* Step 1: Create Account */}
        {step === 1 && (
          <div className="onboarding-card">
            <h2>Create Your Parent Account</h2>
            <p className="onboarding-subtitle">You'll use this to manage your player's training.</p>
            <form onSubmit={handleCreateAccount}>
              {accountError && <div className="order-error">{accountError}</div>}
              <div className="onboarding-field">
                <label>Email</label>
                {(isClubPays || isScholarship || isTrialSignup || isFreeSignup) ? (
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} required className="onboarding-input" autoCapitalize="none" placeholder="your@email.com" />
                ) : (
                  <input type="email" value={email} readOnly className="onboarding-input readonly" />
                )}
              </div>
              <div className="onboarding-row">
                <div className="onboarding-field">
                  <label>Your First Name</label>
                  <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} required className="onboarding-input" />
                </div>
                <div className="onboarding-field">
                  <label>Your Last Name</label>
                  <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} required className="onboarding-input" />
                </div>
              </div>
              <div className="onboarding-field">
                <label>Password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={8} className="onboarding-input" />
              </div>
              <div className="onboarding-field">
                <label>Confirm Password</label>
                <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required className="onboarding-input" />
              </div>
              <div className="onboarding-field" style={{ marginTop: 12 }}>
                <label>Timezone</label>
                <select value={timezone} onChange={e => setTimezone(e.target.value)} className="onboarding-input">
                  {Intl.supportedValuesOf ? Intl.supportedValuesOf('timeZone').map(tz => (
                    <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
                  )) : [
                    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
                    'America/Anchorage', 'Pacific/Honolulu', 'America/Phoenix',
                    'America/Toronto', 'America/Vancouver', 'America/Edmonton',
                    'Europe/London', 'Europe/Paris', 'Europe/Berlin',
                    'Asia/Tokyo', 'Asia/Shanghai', 'Australia/Sydney',
                  ].map(tz => (
                    <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
              <button className="btn btn-primary order-btn" type="submit" disabled={loading} style={{ width: '100%', marginTop: 12 }}>
                {loading ? 'Creating Account...' : 'Create Parent Account'}
              </button>
            </form>
          </div>
        )}

        {/* Step 2: Add Players */}
        {step === 2 && (
          <div className="onboarding-card">
            <h2>Add Your {plan === 'family' ? 'Players' : 'Player'}</h2>
            <p className="onboarding-subtitle">
              {plan === 'family'
                ? `Add up to ${maxPlayers} players. You've added ${players.length} so far.`
                : 'Set up your player\'s profile.'}
            </p>

            {/* Existing players */}
            {players.map((player, i) => (
              <div key={i} className="onboarding-player-added">
                <i className="fas fa-check-circle" style={{ color: '#1eae5a', marginRight: 8 }}></i>
                <strong>{player.first_name ? `${player.first_name} ${player.last_name}` : player.generated_username}</strong>
              </div>
            ))}

            {/* Team credentials card — shown after player is created */}
            {createdPlayerCredentials && (
              <div className="onboarding-credentials-card" style={{
                background: 'var(--bg-card, #1a1a2e)', border: '1px solid var(--orange)', borderRadius: 10,
                padding: 16, marginBottom: 16
              }}>
                <h3 style={{ margin: '0 0 8px', fontSize: '1rem', color: '#fff' }}>
                  <i className="fas fa-key" style={{ color: 'var(--orange)', marginRight: 6 }}></i>
                  Login Info for {createdPlayerCredentials.name}
                </h3>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0 0 10px' }}>
                  Save this information! Your child will need it to log in.
                </p>
                <div style={{ fontSize: '0.9rem', lineHeight: 1.8, color: '#ffffff' }}>
                  <div><strong>Username:</strong> {createdPlayerCredentials.username}</div>
                  <div><strong>Password:</strong> {createdPlayerCredentials.password}</div>
                  <div><strong>Login URL:</strong> {window.location.origin}/player-login</div>
                </div>
                <button type="button" className="btn btn-sm btn-outline" style={{ marginTop: 10, color: '#fff' }}
                  onClick={() => {
                    const text = `Train Daily Login\nUsername: ${createdPlayerCredentials.username}\nPassword: ${createdPlayerCredentials.password}\nLogin: ${window.location.origin}/player-login`;
                    navigator.clipboard.writeText(text).catch(() => {});
                  }}>
                  <i className="fas fa-copy" style={{ marginRight: 4 }}></i> Copy Login Info
                </button>
              </div>
            )}

            {addingPlayer ? (
              <form onSubmit={handleAddPlayer}>
                {playerError && <div className="order-error">{playerError}</div>}
                <div className="onboarding-field">
                  <label>Birth Year</label>
                  <select value={birthYear} onChange={e => setBirthYear(e.target.value)} required className="onboarding-input">
                    <option value="">Select year...</option>
                    {BIRTH_YEARS.map(y => <option key={y} value={y}>{y} (age {CURRENT_YEAR - y})</option>)}
                  </select>
                </div>

                {/* Player name fields */}
                {birthYear && (
                  <div className="onboarding-row">
                    <div className="onboarding-field">
                      <label>Player First Name</label>
                      <input type="text" value={playerFirstName} onChange={e => setPlayerFirstName(e.target.value)} required className="onboarding-input" />
                    </div>
                    <div className="onboarding-field">
                      <label>Player Last Name</label>
                      <input type="text" value={playerLastName} onChange={e => setPlayerLastName(e.target.value)} required className="onboarding-input" />
                    </div>
                    {!isTeamSignup && (
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>Only you see this name. Players log in with a username.</p>
                    )}
                  </div>
                )}

                {/* Individual signup: username selector */}
                {!isTeamSignup && birthYear && playerFirstName && playerLastName && usernameOptions.length > 0 && (
                  <div className="onboarding-field">
                    <label style={{ display: 'flex', alignItems: 'center' }}>Choose a Username
                      <HelpTooltip>
                        <h3>Why random usernames?</h3>
                        <p>To protect the online identity of children, TrainDaily does not use any personally identifiable information. Usernames are chosen at random.</p>
                        <p>If you do not like any of the usernames below, click on the <strong>Shuffle</strong> link and it will allow you to choose from more.</p>
                      </HelpTooltip>
                    </label>
                    <div className="onboarding-username-options">
                      {usernameOptions.map((opt, i) => (
                        <button
                          key={i}
                          type="button"
                          className={`onboarding-username-option ${selectedUsername === opt ? 'selected' : ''}`}
                          onClick={() => setSelectedUsername(opt)}
                        >
                          <strong>{opt.generated_username}</strong>
                        </button>
                      ))}
                    </div>
                    <button type="button" className="onboarding-shuffle" onClick={generateUsernames} disabled={usernameLoading}>
                      <i className="fas fa-shuffle"></i> {usernameLoading ? 'Generating...' : 'Shuffle'}
                    </button>
                  </div>
                )}

                {/* Team signup: password fields */}
                {isTeamSignup && birthYear && playerFirstName && playerLastName && (
                  <>
                    <div className="onboarding-field">
                      <label>Player Password</label>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0 0 4px' }}>
                        Your child will use this password to log in. Minimum 6 characters.
                      </p>
                      <input type="password" value={playerPassword}
                        onChange={e => setPlayerPassword(e.target.value)} required minLength={6} className="onboarding-input"
                        placeholder="At least 6 characters" />
                    </div>
                    <div className="onboarding-field">
                      <label>Confirm Password</label>
                      <input type="password" value={confirmPlayerPassword}
                        onChange={e => setConfirmPlayerPassword(e.target.value)} required className="onboarding-input" />
                    </div>
                  </>
                )}

                {/* Individual signup: password fields */}
                {!isTeamSignup && selectedUsername && (
                  <>
                    <div className="onboarding-field">
                      <label>Password</label>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0 0 4px' }}>
                        Your child will use this password to log in. Minimum 6 characters.
                      </p>
                      <input type="password" value={playerIndividualPassword}
                        onChange={e => setPlayerIndividualPassword(e.target.value)} required minLength={6} className="onboarding-input"
                        placeholder="At least 6 characters" />
                    </div>
                    <div className="onboarding-field">
                      <label>Confirm Password</label>
                      <input type="password" value={confirmPlayerIndividualPassword}
                        onChange={e => setConfirmPlayerIndividualPassword(e.target.value)} required className="onboarding-input" />
                    </div>
                  </>
                )}

                {/* COPPA consent for under-13 */}
                {birthYear && playerFirstName && playerLastName &&
                  (isTeamSignup || selectedUsername) &&
                  (CURRENT_YEAR - parseInt(birthYear)) < 13 && (
                  <div className="onboarding-consent-inline">
                    <label className="onboarding-consent-label">
                      <input type="checkbox" checked={consentAccepted} onChange={e => setConsentAccepted(e.target.checked)} />
                      <span>I am the parent or legal guardian and I consent to my child's use of Train Daily.</span>
                    </label>
                    <div className="onboarding-field" style={{ marginTop: 8 }}>
                      <label>Parent/Guardian Full Name</label>
                      <input type="text" value={consentName} onChange={e => setConsentName(e.target.value)}
                        className="onboarding-input" placeholder="Your full name" />
                    </div>
                  </div>
                )}

                <button className="btn btn-primary order-btn" type="submit" disabled={loading || !birthYear || !playerFirstName || !playerLastName || (isTeamSignup ? (playerPassword.length < 6) : (!selectedUsername || playerIndividualPassword.length < 6))} style={{ width: '100%', marginTop: 12 }}>
                  {loading ? 'Adding...' : 'Add Player'}
                </button>
              </form>
            ) : (
              <div style={{ marginTop: 16 }}>
                {players.length < maxPlayers && plan === 'family' && (
                  <button className="btn btn-secondary order-btn" onClick={() => setAddingPlayer(true)} style={{ width: '100%', marginBottom: 12 }} type="button">
                    Add Another Player
                  </button>
                )}
                <button className="btn btn-primary order-btn" onClick={handlePlayersDone} style={{ width: '100%' }} type="button">
                  {players.length === 0 ? 'Add at least one player to continue' : 'Continue'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Consent */}
        {step === 3 && (
          <div className="onboarding-card">
            <h2>Parental Consent</h2>
            <p className="onboarding-subtitle">Train Daily is designed for children. As the parent or guardian, we need your consent.</p>
            <label className="onboarding-consent-label">
              <input type="checkbox" checked={consented} onChange={e => setConsented(e.target.checked)} />
              <span>I am the parent or legal guardian and I consent to my child's use of Train Daily. I understand that I can manage or delete my child's account at any time from my parent dashboard.</span>
            </label>
            <button className="btn btn-primary order-btn" onClick={handleConsent} disabled={!consented} style={{ width: '100%', marginTop: 20 }} type="button">
              Continue
            </button>
          </div>
        )}

        {/* Step 4: PWA Install */}
        {step === 4 && (
          <div className="onboarding-card">
            <PwaInstall onDone={handleDone} doneLabel="Go to Dashboard" showUsageInstructions={true} />
          </div>
        )}
      </div></main>
    </div>
  );
}
